<script setup lang="ts">
  import { markFirstAdRequest } from '../../utils/consent/firstAdRequest'
  import { computed, onMounted } from 'vue'
  import type { NtpAdEvent } from '../../composables/analytics/types'
  import type { AssertiveYieldManagerEnv, Slot } from '../../types/assertive'
  import { getIndexOfPlacement } from '../../utils/ad-ops/adFunctions'
  import {
    destroyedAyInstanceCodes,
    refreshedAyInstanceCodes,
  } from '../../utils/ad-ops/ayInstanceCodes'
  import {
    appDrivesPlacement,
    DISPLAY_MARGIN_FLAG,
    driveSlot,
    forgetRequestedSlot,
    marginPx,
    RENDER_MARGIN_FLAG,
    tagRumWithAppOwned,
    tagRumWithMargins,
    trackRequestedSlots,
    VIEWPORT_EDGE,
    whenWithin,
    type SlotDriver,
    type SlotMargins,
  } from '../../utils/ad-ops/assertive-yield/slotTiming'
  import debug from '../../utils/console'
  import { withReadyAyManager } from '../../utils/consent/adReadiness'
  import { withAyManager } from '../../utils/ad-ops/assertive-yield/queue'
  import {
    markConsentTiming,
    measureConsentTiming,
  } from '../../utils/consent/consentTiming'

  const logger = debug.withTag('Assertive')

  interface AssertiveAdAttributes {
    'data-render-registered': boolean
  }
  export interface AssertiveProps {
    placement: string
    tilePosition?: number
    lazyRender?: boolean
    sticky?: boolean
    adHeight?: string
    adWidth?: string
    adInstanceId?: string
  }

  const props = withDefaults(defineProps<AssertiveProps>(), {
    tilePosition: -1,
    lazyRender: true,
    sticky: false,
    adHeight: 'auto',
    adWidth: 'auto',
    adInstanceId: '',
  })

  const { adClicked, renderWhenCurrentElementIsVisible } = useAdFunctions()
  const { $ayLoaded, $ayManagerEnv } = useNuxtApp()
  const placementEl = useTemplateRef<HTMLElement | null>('placementEl')
  const placementClassName = ref<string | null>(props.placement)

  const renderAYElement = ref<boolean>(false)
  const adUnitPath = ref<string>('')
  let instanceCode = ''
  let displayedIn: HTMLElement | null = null
  let unmounted = false
  let cancelPendingDisplay = () => {}
  let marginsReady: Promise<SlotMargins> = Promise.resolve(VIEWPORT_EDGE)
  let stopDisplayWatch: (() => void) | null = null
  let slotDriver: SlotDriver | null = null
  const placementAttributes = computed<AssertiveAdAttributes>(() => {
    return {
      'data-render-registered': false,
    }
  })

  function getEventData(): NtpAdEvent {
    return {
      ntp_tile_position: props.tilePosition,
      ntp_ad_brand: 'programmatic',
      ntp_ad_provider: 'programmatic',
      ntp_creative_id: adUnitPath.value,
      ntp_placement_name: props.placement,
      ntp_ad_refresh: false,
      ntp_ay_instance_code: instanceCode,
    }
  }

  let adEngaged = false
  let hasClicked = false

  function onMouseOver() {
    adEngaged = true
  }

  function onMouseOut() {
    adEngaged = false
    hasClicked = false
  }

  function isFocusInsideAd() {
    return placementEl.value?.contains(document.activeElement) ?? false
  }

  function onLeaveEvent() {
    if (!adEngaged || hasClicked || !isFocusInsideAd()) return
    hasClicked = true
    adClicked(getEventData(), adUnitPath.value)
  }

  function onVisibilityChange() {
    if (document.visibilityState === 'hidden') onLeaveEvent()
  }

  const renderAYAd = async () => {
    if (
      !placementEl.value ||
      placementEl.value?.getAttribute('data-render-registered') === 'true' ||
      renderAYElement.value
    )
      return
    renderAYElement.value = true
    placementEl.value?.setAttribute('data-render-registered', 'true')

    const index = getIndexOfPlacement(props.placement, placementEl.value)

    placementClassName.value =
      index > 0
        ? `${props.placement} ${props.placement}-${index}`
        : props.placement

    markConsentTiming('first-visible-ad-wait-start', true)
    await $ayLoaded
    const container = placementEl.value
    if (unmounted || !container?.isConnected) return

    cancelPendingDisplay = withReadyAyManager(withAyManager, () => {
      const env = $ayManagerEnv
      if (!env || unmounted || !container.isConnected) return
      markConsentTiming('first-visible-ad-ready', true)
      measureConsentTiming(
        'first-visible-ad-wait',
        'first-visible-ad-wait-start',
        'first-visible-ad-ready'
      )
      displayedIn = container
      env.onEvent('afterDefineSlot', onAfterDefineSlot)
      markConsentTiming('first-visible-ad-display', true)
      markFirstAdRequest('display')
      tagRumWithAppOwned(env)
      const appOwned = appDrivesPlacement(env, props.placement)
      if (appOwned) trackRequestedSlots()
      env.display(container)
      if (appOwned) startDriver(container, env)
    })
  }

  // display() has started the auction; the app owns the render. The auction
  // can define the slot before the flags load, so a code that already arrived
  // is handed over.
  function startDriver(
    container: HTMLElement,
    env: Pick<AssertiveYieldManagerEnv, 'render'>
  ) {
    marginsReady.then((margins) => {
      if (unmounted) return
      slotDriver = driveSlot(container, margins.renderPx, env)
      if (instanceCode) slotDriver.defined(instanceCode)
    })
  }

  // Copies of a placement share its name, so claim the slot by containment.
  // Searched from the container because it may already be detached.
  function onAfterDefineSlot(code: string, slot: Slot) {
    if (instanceCode || !displayedIn) return
    const slotId = slot.getSlotElementId()
    const ownsSlot = Array.from(displayedIn.querySelectorAll('[id]')).some(
      (el) => el.id === slotId
    )
    if (!ownsSlot) return
    instanceCode = code
    adUnitPath.value = code
    forgetRequestedSlot(code)
    slotDriver?.defined(code)
    // Defined after unmount, so nothing else will destroy it.
    if (unmounted) destroyInstance()
  }

  // Until AY defines the slot there is no code to destroy, so the listener
  // stays until it arrives.
  function destroyInstance() {
    slotDriver?.stop()
    $ayManagerEnv?.cmd.push(() => {
      const code = instanceCode
      if (!code) return
      refreshedAyInstanceCodes.delete(code)
      // The native fallback already destroyed it.
      if (!destroyedAyInstanceCodes.delete(code)) {
        $ayManagerEnv?.destroy([code], { useInstanceCodes: true })
      }
      $ayManagerEnv?.offEvent('afterDefineSlot', onAfterDefineSlot)
    })
  }

  onMounted(() => {
    placementEl.value?.addEventListener('mouseenter', onMouseOver, {
      passive: true,
    })
    placementEl.value?.addEventListener('mouseleave', onMouseOut, {
      passive: true,
    })
    window.addEventListener('blur', onLeaveEvent, { passive: true })
    window.addEventListener('pagehide', onLeaveEvent, { passive: true })
    window.addEventListener('beforeunload', onLeaveEvent, { passive: true })
    document.addEventListener('visibilitychange', onVisibilityChange, {
      passive: true,
    })

    marginsReady = resolveMargins()
    marginsReady.then(tagRumWithMargins)
    // Watch for visibility now rather than after the manager loads, so a slot
    // that is only briefly in view while the feed lays out is not missed.
    renderWhenCurrentElementIsVisible(
      placementEl.value,
      props.lazyRender,
      props.sticky,
      renderAYAd
    )
    if (props.lazyRender) {
      displayAheadIfAppOwned().catch((err) => {
        logger.error('Unable to display AY slot', err)
      })
    }
  })

  async function resolveMargins(): Promise<SlotMargins> {
    try {
      // Read once the provider has loaded: an earlier read gets the default.
      const { loaded } = await useOpenFeature()
      const client = await loaded
      return {
        displayPx: marginPx(client.getNumberValue(DISPLAY_MARGIN_FLAG, 0)),
        renderPx: marginPx(client.getNumberValue(RENDER_MARGIN_FLAG, 0)),
      }
    } catch (err) {
      logger.warn('Slot margin flags unavailable, using the viewport edge', err)
      return VIEWPORT_EDGE
    }
  }

  // An app-owned slot is displayed, which starts its auction, at the display
  // margin. The visibility gate stays as the fallback; renderAYAd runs once.
  async function displayAheadIfAppOwned() {
    const [ayReady, margins] = await Promise.all([$ayLoaded, marginsReady])
    const container = placementEl.value
    if (!ayReady || unmounted || !container || margins.displayPx === 0) return
    // Inside the queue, like renderAYAd, so both read the settings AY settled on.
    $ayManagerEnv?.cmd.push(() => {
      if (unmounted || !appDrivesPlacement($ayManagerEnv, props.placement)) {
        return
      }
      stopDisplayWatch = whenWithin(container, margins.displayPx, () => {
        renderAYAd().catch((err) => {
          logger.error('Unable to display AY slot', err)
        })
      })
    })
  }

  onUnmounted(() => {
    placementEl.value?.removeEventListener('mouseenter', onMouseOver)
    placementEl.value?.removeEventListener('mouseleave', onMouseOut)
    window.removeEventListener('blur', onLeaveEvent)
    window.removeEventListener('pagehide', onLeaveEvent)
    window.removeEventListener('beforeunload', onLeaveEvent)
    document.removeEventListener('visibilitychange', onVisibilityChange)
    unmounted = true
    cancelPendingDisplay()
    stopDisplayWatch?.()
    slotDriver?.stop()
    if (renderAYElement.value) destroyInstance()
    renderAYElement.value = false
  })
</script>

<template>
  <div
    :id="props.placement"
    ref="placementEl"
    :class="placementClassName"
    :data-ay-manager-id="props.placement"
    v-bind="placementAttributes"
    :data-ad-instance-id="props.adInstanceId"
  />
</template>
